import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/* ============================================================
   NavRail — menu de navigation PARTAGÉ (source unique de vérité).
   Remplace les rails .sb dupliqués dans chaque page.
   - Desktop : rail vertical d'icônes (hérite des styles .sb/.sb-item
     déjà définis par chaque page → look identique, accent par page).
   - Mobile (≤680px) : le rail disparaît, une barre de navigation
     apparaît en bas de l'écran (icônes seules).
   ============================================================ */

const PRIMARY = [
  { path: '/cockpit',   icon: '▦', label: 'Cockpit' },
  { path: '/positions', icon: '⊞', label: 'Positions' },
  { path: '/creators',  icon: '★', label: 'Créateurs' },
  { path: '/wallets',   icon: '⦿', label: 'Wallets' },
];
const SECONDARY = [
  { path: '/performance', icon: '📈', label: 'Performance' },
  { path: '/tokens',      icon: '◎',  label: 'Tokens' },
  { path: '/sante',       icon: '♥',  label: 'Santé' },
  { path: '/backtests',   icon: '⏱',  label: 'Backtests' },
  { path: '/discovery',   icon: '🔎', label: 'Découverte' },
];
// rail desktop = ordre demandé : cockpit, performance, positions, tokens, creators, wallets, backtests, santé, découverte, (users en admin via concat)
const ITEMS = [PRIMARY[0], SECONDARY[0], PRIMARY[1], SECONDARY[1], PRIMARY[2], PRIMARY[3], SECONDARY[3], SECONDARY[2], SECONDARY[4]];

const NAV_CSS = `
.nav-mobile{display:none;}
@media(max-width:680px){
  /* la racine passe en colonne, le rail vertical disparaît, la barre basse apparaît */
  .ck,.cr,.cw,.uk{flex-direction:column !important;}
  aside.sb{display:none !important;}
  .nav-mobile{
    display:flex;order:99;flex:0 0 auto;
    align-items:stretch;justify-content:space-around;gap:2px;
    padding:7px 4px;overflow-x:auto;-webkit-overflow-scrolling:touch;
    background:#0c0d11;border-top:1px solid var(--border);
  }
  .nav-mobile .nm-item{
    flex:1 0 auto;min-width:42px;
    display:flex;align-items:center;justify-content:center;
    background:transparent;border:none;color:var(--text2);
    font-family:inherit;font-size:19px;cursor:pointer;
    padding:8px 2px;border-radius:8px;
  }
  .nav-mobile .nm-item.on{color:var(--green);background:var(--surface);}
  .nav-mobile .nm-item:active{background:var(--surface2);}
  .nav-sheet-ovl{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:120;display:flex;align-items:flex-end;}
  .nav-sheet{width:100%;background:#0c0d11;border-top:1px solid var(--border);border-radius:14px 14px 0 0;padding:8px 8px 16px;}
  .nav-sheet .ns-item{display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;color:var(--text);font-family:inherit;font-size:14px;padding:13px 14px;border-radius:9px;cursor:pointer;}
  .nav-sheet .ns-item:active{background:var(--surface);}
  .nav-sheet .ns-item .ic{font-size:18px;width:24px;text-align:center;}
}
`;

export default function NavRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isOn = (p) => location.pathname === p;
  const [sheet, setSheet] = useState(false);

  const items = ITEMS.concat(
    user?.role === 'admin' ? [{ path: '/users', icon: '👥', label: 'Users' }] : []
  );

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <>
      <style>{NAV_CSS}</style>

      {/* Rail vertical — desktop (styles .sb/.sb-item hérités de la page) */}
      <aside className="sb">
        <div className="sb-logo">N</div>
        {items.map((it) => (
          <button
            key={it.path}
            className={`sb-item ${isOn(it.path) ? 'on' : ''}`}
            onClick={() => navigate(it.path)}
            title={it.label}
          >
            <span style={{ fontSize: 17 }}>{it.icon}</span>
            <span className="tip">{it.label}</span>
          </button>
        ))}
        <div className="sb-spacer" />
        <button className="sb-item" onClick={handleLogout} title="Déconnexion">
          <span style={{ fontSize: 16 }}>⎋</span><span className="tip">Déconnexion</span>
        </button>
      </aside>

      {/* Barre de navigation — mobile : 4 primaires + Plus */}
      <nav className="nav-mobile">
        {PRIMARY.map((it) => (
          <button key={it.path} className={`nm-item ${isOn(it.path) ? 'on' : ''}`} onClick={() => navigate(it.path)} title={it.label} aria-label={it.label}>{it.icon}</button>
        ))}
        <button className={`nm-item ${SECONDARY.some(x => isOn(x.path)) ? 'on' : ''}`} onClick={() => setSheet(true)} title="Plus" aria-label="Plus">⋯</button>
      </nav>

      {sheet && (
        <div className="nav-sheet-ovl" onClick={() => setSheet(false)}>
          <div className="nav-sheet" onClick={(e) => e.stopPropagation()}>
            {SECONDARY.map((it) => (
              <button key={it.path} className="ns-item" onClick={() => { navigate(it.path); setSheet(false); }}>
                <span className="ic">{it.icon}</span>{it.label}
              </button>
            ))}
            {user?.role === 'admin' && (
              <button className="ns-item" onClick={() => { navigate('/users'); setSheet(false); }}><span className="ic">👥</span>Users</button>
            )}
            <button className="ns-item" onClick={handleLogout}><span className="ic">⎋</span>Déconnexion</button>
          </div>
        </div>
      )}
    </>
  );
}
